'use strict';

let map;
let marker;
let location = {};
var googleMapsLoader = require('./googleMapsLoader');

/**
 * Parses Google Maps address components to a structured address object.
 *
 * @param {Array} components - Array of address components from Google Maps API.
 * @returns {Object} Parsed address containing address1, address2, city, state, pincode, country, and formatted_address.
 */
function parseAddressComponents(components) {
    let address1 = '';
    let address2 = '';
    let address3 = '';
    let city = '';
    let state = '';
    let pincode = '';
    let country = '';

    components.forEach(component => {
        const types = component.types;

        if (types.includes('premise') && !address1) {
            address1 = component.long_name;
        } else if (types.includes('route') && !address2) {
            address2 = component.short_name;
        } else if (types.includes('sublocality_level_1') && !address3) {
            address3 = component.short_name;
        } else if (types.includes('locality') && !city) {
            city = component.short_name;
        } else if (types.includes('administrative_area_level_1') && !state) {
            state = component.long_name;
        } else if (types.includes('postal_code') && !pincode) {
            pincode = component.short_name;
        } else if (types.includes('country') && !country) {
            country = component.long_name;
        }
    });

    const combinedAddress2 = address3 ? `${address2}, ${address3}` : address2;
    const parts = [address1, combinedAddress2, city, state, country];
    let formattedaddress = '';

    parts.forEach((item) => {
        if (typeof item === 'string') {
            const cleanItem = item.replace(/,/g, '').trim();
            if (cleanItem) {
                formattedaddress = formattedaddress
                    ? formattedaddress + ', ' + cleanItem
                    : cleanItem;
            }
        }
    });

    return {
        address1,
        address2: combinedAddress2,
        city,
        state,
        pincode,
        country,
        formattedaddress
    };
}

/**
 * Formats distance from meters to string with units.
 *
 * @param {number} distanceMeters - Distance in meters.
 * @returns {string} Formatted distance with meters (M) or kilometers (KM).
 */
function formatDistance(distanceMeters) {
    if (distanceMeters < 1000) {
        return `${Math.round(distanceMeters)} M`;
    }
    return `${(distanceMeters / 1000).toFixed(2)} KM`;
}

/**
 * Computes and displays the distance between the marker and the current user location.
 *
 * @returns {void}
 */
function getDistanceFromCurrentLocation() {
    if (!map || !navigator.geolocation) {
        return;
    }

    navigator.geolocation.getCurrentPosition((position) => {
        const currentPos = new google.maps.LatLng(position.coords.latitude, position.coords.longitude); // eslint-disable-line no-undef
        const mapCenter = map.getCenter();
        if (!mapCenter) {
            return;
        }

        const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(currentPos, mapCenter); // eslint-disable-line no-undef

        const formattedDistance = formatDistance(distanceMeters);

        $('#distanceDisplay').text(`Your Pin location is ${formattedDistance} away from current location`);
    }, () => {
        if (!$('.location-permission-error').hasClass('opened-once') && $('#tryAtHomeModal').hasClass('enabled')) {
            $('.location-permission-error').removeClass('d-none');
        }
    });
}

/**
 * Updates address display based on the current marker position using reverse geocoding.
 *
 * @param {google.maps.Geocoder} geocoder - Google Maps Geocoder instance.
 * @returns {void}
 */
function updateAddressFromMarkerPosition(geocoder) {
    if (!map) return;

    const position = map.getCenter();
    if (!position) return;

    geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results[0]) {
            const addressComponents = results[0].address_components;

            const countryComponent = addressComponents.find(component => component.types.includes('country'));

            if (countryComponent && countryComponent.short_name === 'IN') {
                location = parseAddressComponents(addressComponents);
                $('#locationFromMap').text(location.formattedaddress);
            } else {
                $('#locationFromMap').text('Please select a location within India');
            }
        } else {
            $('#locationFromMap').text('Address not found');
        }
    });
}

/**
 * Initializes the Google Map, autocomplete, marker, and event listeners.
 *
 * @returns {Object|null} Map instance when available, otherwise null.
 */
function initializeMap() {
    if (map) {
        return map;
    }

    const defaultLocation = { lat: 12.9716, lng: 77.5946 };
    const geocoder = new google.maps.Geocoder(); // eslint-disable-line no-undef

    var mapElement = document.getElementById('map');
    if (!mapElement) {
        return null;
    }

    map = new google.maps.Map(mapElement, { // eslint-disable-line no-undef
        center: defaultLocation,
        zoom: 20,
        streetViewControl: false,
        zoomControl: true,
        rotateControl: false,
        mapTypeControl: true,
        fullscreenControl: true,
        scaleControl: false,
        disableDefaultUI: false,
        cameraControl: false
    });

    var mapMarkerURL = $('#map-marker-url').val();
    marker = new google.maps.Marker({ // eslint-disable-line no-undef
        position: defaultLocation,
        map: map,
        clickable: false,
        icon: {
            url: mapMarkerURL,
            scaledSize: new google.maps.Size(40, 40), // eslint-disable-line no-undef
            origin: new google.maps.Point(0, 0), // eslint-disable-line no-undef
            anchor: new google.maps.Point(20, 40) // eslint-disable-line no-undef
        }
    });

    map.addListener('center_changed', () => {
        if (marker) {
            marker.setPosition(map.getCenter());
        }
    });

    map.addListener('dragend', () => {
        updateAddressFromMarkerPosition(geocoder);
        getDistanceFromCurrentLocation();
    });

    const $input = $('#pac-input');
    const autocomplete = new google.maps.places.Autocomplete($input[0], { // eslint-disable-line no-undef
        componentRestrictions: { country: ['in'] }
    });

    autocomplete.bindTo('bounds', map);
    autocomplete.setComponentRestrictions({
        country: ['in']
    });

    autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (!place.geometry || !place.geometry.location) {
            return;
        }

        const locationLatLng = place.geometry.location;

        if (place.geometry.viewport) {
            map.fitBounds(place.geometry.viewport);
        } else {
            map.setCenter(locationLatLng);
            map.setZoom(15);
        }

        location = parseAddressComponents(place.address_components);
        $('#locationFromMap').text(location.formattedaddress);
    });

    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };

                map.setCenter(pos);
                map.setZoom(15);

                updateAddressFromMarkerPosition(geocoder);
                getDistanceFromCurrentLocation();
            },
            () => {
                // required for lint
            }
        );
    }

    return map;
}

/**
 * Lazily loads the Google Maps SDK and initializes the map once available.
 *
 * @returns {Promise<Object>} Resolves with the map instance.
 */
function initMap() {
    return googleMapsLoader.load().then(function () {
        return initializeMap();
    });
}

/**
 * Updates the given input element with the current marker's lat and lng as JSON string.
 *
 * @param {jQuery} $this - jQuery object of the input element to update.
 * @returns {void}
 */
function updateLatLng($this) {
    if (map && map.getCenter()) {
        let pos = map.getCenter();
        location.lat = pos.lat().toFixed(6);
        location.lng = pos.lng().toFixed(6);
        $this.val(JSON.stringify(location));
    }
}

/**
 * Gets the current device location and reverse geocodes it to an address object.
 *
 * @returns {Promise<{address: Object}>} Resolves with the address object containing lat/lng and parsed address fields.
 */
function getCurrentLocationWithAddress() {
    return googleMapsLoader.load().then(function () {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                var message = $('#resourceGeoNotSupported').val();
                reject(new Error(message));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    const latlng = { lat, lng };

                    const geocoder = new google.maps.Geocoder(); // eslint-disable-line no-undef

                    geocoder.geocode({ location: latlng }, (results, status) => {
                        if (status === 'OK' && results[0]) {
                            const parsedAddress = parseAddressComponents(results[0].address_components);
                            parsedAddress.lat = lat;
                            parsedAddress.lng = lng;
                            resolve({
                                address: parsedAddress
                            });
                        } else {
                            var unableToRetrive = $('#resourceGeoUnableToRetrive').val();
                            reject(new Error(unableToRetrive));
                        }
                    });
                },
                (error) => {
                    reject(new Error(`Geolocation error: ${error.message}`));
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        });
    });
}

module.exports = {
    initMap: initMap,
    updateLatLng: updateLatLng,
    getCurrentLocationWithAddress: getCurrentLocationWithAddress
};
